import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as productsApi from './api'
import type { ConfigurationRequest, ConfigurationUpdateRequest } from './types'

export const productKeys = {
  list: (activeOnly: boolean) => ['products', activeOnly] as const,
}

export function useConfigurations(activeOnly = true) {
  return useQuery({ queryKey: productKeys.list(activeOnly), queryFn: () => productsApi.listConfigurations(activeOnly) })
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['products'] })
}

export function useCreateConfiguration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: ConfigurationRequest) => productsApi.createConfiguration(p),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useUpdateConfiguration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: ConfigurationUpdateRequest }) =>
      productsApi.updateConfiguration(vars.code, vars.payload),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useDeleteConfiguration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => productsApi.deleteConfiguration(code),
    onSuccess: () => invalidateAll(qc),
  })
}

export function useSetConfigurationSections(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => productsApi.setConfigurationSections(configurationCode, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'forms', 'schema'] })
      qc.invalidateQueries({ queryKey: ['formSchema'] })
      qc.invalidateQueries({ queryKey: ['contracts'] })
    },
  })
}

export function useSetConfigurationStatusTracks(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => productsApi.setConfigurationStatusTracks(configurationCode, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['statuses'] })
    },
  })
}
